import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Calendar, Users, Phone, MessageCircle, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { CONTACT } from '@/lib/brand';

/**
 * BookingModal — universal booking dialog linked to a business.
 * Submits to /api/bookings (admin sees it instantly in /business-panel)
 * AND offers an "Also send via WhatsApp" CTA after submission.
 */
export const BookingModal = ({ open, onOpenChange, business }) => {
  const [form, setForm] = useState({
    customer_name: '',
    phone: '',
    date: '',
    time: '',
    guests: 2,
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!business) return null;
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const reset = () => {
    setForm({ customer_name: '', phone: '', date: '', time: '', guests: 2, message: '' });
    setSuccess(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.phone || !form.date) {
      return toast.error('Please fill name, phone and date.');
    }
    setLoading(true);
    try {
      await api.booking({
        business_id: business.id,
        business_name: business.name,
        customer_name: form.customer_name,
        phone: form.phone,
        date: form.date,
        time: form.time,
        guests: Number(form.guests),
        message: form.message,
      });
      setSuccess(true);
      toast.success('Booking request received! Our team will contact you soon.');
    } catch (err) {
      toast.error('Could not submit booking. Try WhatsApp instead.');
    } finally {
      setLoading(false);
    }
  };

  const waMessage = `Booking request — ${business.name}\nName: ${form.customer_name}\nPhone: ${form.phone}\nDate: ${form.date} ${form.time}\nGuests: ${form.guests}\n${form.message ? `Notes: ${form.message}` : ''}`;

  const handleOpenChange = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(reset, 250);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="booking-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Book {business.name}</DialogTitle>
          <DialogDescription className="text-sm">
            We'll contact you on WhatsApp within minutes to confirm your booking.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-6 text-center space-y-4" data-testid="booking-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#00AA6C]/10 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-[#00AA6C]" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-bold">Booking received!</h3>
              <p className="text-sm text-gray-600 mt-1">
                We've saved your request for <strong>{business.name}</strong>.
                Want a faster reply? Send us a quick WhatsApp now.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Button variant="green" size="lg" className="w-full" asChild data-testid="booking-also-whatsapp">
                <a href={CONTACT.whatsappQuick(waMessage)} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-4 h-4" /> Send via WhatsApp
                </a>
              </Button>
              <Button variant="outline" onClick={() => handleOpenChange(false)} data-testid="booking-close">Close</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label htmlFor="bm-name">Your name</Label>
              <Input id="bm-name" required value={form.customer_name} onChange={update('customer_name')} placeholder="Jane Doe" data-testid="booking-name" />
            </div>
            <div>
              <Label htmlFor="bm-phone">Phone (WhatsApp preferred)</Label>
              <Input id="bm-phone" required type="tel" value={form.phone} onChange={update('phone')} placeholder="+355 6X XXX XXXX" data-testid="booking-phone" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label htmlFor="bm-date" className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Date</Label>
                <Input id="bm-date" required type="date" value={form.date} onChange={update('date')} data-testid="booking-date" />
              </div>
              <div>
                <Label htmlFor="bm-time">Time</Label>
                <Input id="bm-time" type="time" value={form.time} onChange={update('time')} data-testid="booking-time" />
              </div>
            </div>
            <div>
              <Label htmlFor="bm-guests" className="flex items-center gap-1"><Users className="w-3 h-3" /> Guests</Label>
              <Input id="bm-guests" type="number" min={1} max={50} value={form.guests} onChange={update('guests')} data-testid="booking-guests" />
            </div>
            <div>
              <Label htmlFor="bm-message">Notes (optional)</Label>
              <Textarea id="bm-message" rows={2} value={form.message} onChange={update('message')} placeholder="Anniversary, sea view, dietary needs…" data-testid="booking-message" />
            </div>
            <Button type="submit" variant="green" size="lg" className="w-full" disabled={loading} data-testid="booking-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
              Request booking
            </Button>
            <p className="text-[11px] text-gray-500 text-center">
              <Phone className="w-3 h-3 inline mr-1" /> Prefer to talk? Call {CONTACT.phoneDisplay}
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Convenience trigger that wraps a button with the modal state
export const BookingButton = ({ business, label = 'Book now', variant = 'green', size = 'sm', className = '', testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant={variant} size={size} className={className} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }} data-testid={testid || `book-${business?.id || 'unknown'}`}>
        <Calendar className="w-4 h-4" /> {label}
      </Button>
      <BookingModal open={open} onOpenChange={setOpen} business={business} />
    </>
  );
};
